<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  provide,
  ref,
  shallowRef,
  useId,
  watch,
  type Component,
} from 'vue';
import { loadingStateInjectionKey, type InputStatus, type InputStatusState } from './internal';
import { default as NxLoadingWrapperIcon } from './NxLoadingWrapperIcon.vue';
import { NxTooltip } from '../Tooltip';

const SHOW_RESULT_DURATION_MS = 2000;
const FADE_OUT_DURATION_MS = 500;

const props = withDefaults(
  defineProps<{
    loading: boolean;
    error: boolean;
    relatedLoading?: boolean;
    /** Error message shown in the tooltip on the error icon. */
    errorMessage?: string;
    /** Keep the default check icon, or expose success for a host-owned border. */
    successPresentation?: 'icon' | 'border';
    /** Keep the default error icon, or expose failure for a host-owned border. */
    errorPresentation?: 'icon' | 'border';
    /** Pass bounded column height through to a filling field or editor. */
    fill?: boolean;
  }>(),
  { successPresentation: 'icon', errorPresentation: 'icon' }
);
defineSlots<{
  default(props: {
    /** Whether the element inside should be disabled due to loading */
    disabled: boolean;
    /** Should be used so the loading/success/error icon can be placed correctly. Input should do this automatically. */
    anchorName: string;
  }): Component;
}>();
const anchorName = `--loading-wrapper-anchor-${useId()}`;

function buildStatus(state: InputStatusState, fadeOut = false): InputStatus {
  return {
    state,
    fadeOut,
    errorMessage: state === 'error' ? props.errorMessage : undefined,
    disabled: state === 'loading' ? true : undefined,
    textHidden: props.relatedLoading ? true : false,
  };
}

const initialState: InputStatusState =
  props.loading || props.relatedLoading ? 'loading' : props.error ? 'error' : 'idle';
const status = shallowRef<InputStatus>(buildStatus(initialState));
// Counted, not flagged: a child input inside a dialog or behind `v-if` leaves
// again, and the wrapper's own icon has to come back with it.
const childInputCount = ref(0);
function registerChildInput(): () => void {
  childInputCount.value++;
  let registered = true;
  return () => {
    if (!registered) return;
    registered = false;
    childInputCount.value--;
  };
}

const showOwnIcon = computed(
  () =>
    childInputCount.value === 0 &&
    status.value.state !== 'idle' &&
    !(status.value.state === 'success' && props.successPresentation === 'border') &&
    !(status.value.state === 'error' && props.errorPresentation === 'border')
);
const ownIconPosition = {
  position: 'absolute',
  positionAnchor: anchorName,
  top: `calc(anchor(top) + 0.5em)`,
  right: `calc(anchor(right) + 0.5em)`,
} as const;

let successFadeTimer: ReturnType<typeof setTimeout> | null = null;
let successIdleTimer: ReturnType<typeof setTimeout> | null = null;

function clearSuccessTimers() {
  if (successFadeTimer) clearTimeout(successFadeTimer);
  if (successIdleTimer) clearTimeout(successIdleTimer);
  successFadeTimer = null;
  successIdleTimer = null;
}

watch(
  () => [props.loading, props.relatedLoading, props.error],
  ([loading, relatedLoading, error], [oldLoading, oldRelatedLoading]) => {
    // A pending success countdown must never outlive a newer state change.
    clearSuccessTimers();

    if (loading || relatedLoading || error) {
      status.value = buildStatus(loading || relatedLoading ? 'loading' : 'error');
      return;
    }
    // Only show success feedback after a real load just finished; a bare
    // error-clear (no preceding load) returns quietly to idle.
    if (!oldLoading && !oldRelatedLoading) {
      status.value = buildStatus('idle');
      return;
    }
    if (oldRelatedLoading) {
      status.value = buildStatus('idle');
      return;
    }
    status.value = buildStatus('success');
    successFadeTimer = setTimeout(
      () => {
        status.value = buildStatus('success', true);
      },
      Math.max(0, SHOW_RESULT_DURATION_MS - FADE_OUT_DURATION_MS)
    );
    successIdleTimer = setTimeout(() => {
      status.value = buildStatus('idle');
    }, SHOW_RESULT_DURATION_MS);
  }
);

// A field that stays in error can be refused again for another reason (0, then
// cleared): the tooltip must follow the new message. A watcher of its own, so a
// message change during the success fade-out cannot reset the status to idle.
watch(
  () => props.errorMessage,
  () => {
    if (status.value.state === 'error') status.value = buildStatus('error');
  }
);

onBeforeUnmount(clearSuccessTimers);

provide(loadingStateInjectionKey, {
  status,
  successPresentation: props.successPresentation,
  errorPresentation: props.errorPresentation,
  register: registerChildInput,
});
</script>

<template>
  <div
    class="loading-state-icon-wrapper"
    :class="{ 'is-fill': props.fill }"
    :data-status="status.state"
    :data-fade-out="status.fadeOut || undefined"
    :data-success-presentation="props.successPresentation"
    :data-error-presentation="props.errorPresentation"
  >
    <slot :disabled="status.state === 'loading'" :anchor-name="anchorName" />
    <!-- Content that is no kit input (a Write editor) has only this icon: its error message
         shows on hover, as at a kit input's status icon. -->
    <NxTooltip
      v-if="showOwnIcon && status.state === 'error' && status.errorMessage"
      :text="status.errorMessage"
      side="top"
      :style="{ ...ownIconPosition, 'pointer-events': 'auto' }"
    >
      <NxLoadingWrapperIcon
        state="error"
        :fade-out="false"
        :fade-out-duration="`${FADE_OUT_DURATION_MS}ms`"
      />
    </NxTooltip>
    <NxLoadingWrapperIcon
      v-else-if="showOwnIcon"
      :state="status.state"
      :fade-out="status.fadeOut"
      :fade-out-duration="`${FADE_OUT_DURATION_MS}ms`"
      :style="{ ...ownIconPosition, 'pointer-events': 'none' }"
    />
  </div>
</template>

<style scoped>
.loading-state-icon-wrapper {
  display: contents;
}

.loading-state-icon-wrapper.is-fill {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
}
</style>
